import { extendedArea } from '@bpd-library/utilities';
import {
    renderProjectPhaseDetail,
    renderPropertyTypeDetail,
} from '@features/area-subscription/components/area-subscription-list-card';
import {
    AreaSubscriptionListCardInterface,
    AreaSubscriptionListDataFilterInterface,
    AreaSubscriptionListFilter,
    AreaSubscriptionListHeaderInterface,
    AreaSubscriptionListItemType,
    AreaSubscriptionListProjectPhaseData,
    AreaSubscriptionListPropertyTypeData,
    AreaSubscriptionListTranslation,
    ExtendedAreaSubscriptionState,
} from '@features/area-subscription/types';
import { getSubscriptionState } from '@store/subscription/store';
import { formatProjectPhaseStatus, formatPropertyTypeStatus } from '@utilities/api-formatters';
import { translateItem, translateItems } from '@utilities/hooks';

import { getTranslationOptions } from '../translation-helpers';

const projectPhaseListTranslationKeys: {
    [K in AreaSubscriptionListFilter]: AreaSubscriptionListTranslation;
} = {
    subscribed: {
        listTitle: 'AreaSubscription.Modal.Step1.SubscribedListTitle',
    },
    available: {
        listTitle: 'AreaSubscription.Modal.Step1.AvailableListTitle',
        selectAllToggleLabel: 'AreaSubscription.Modal.Step1.SelectAllToggleLabel',
        selectItemToggleLabel: 'AreaSubscription.Modal.Step1.SelectItemToggleLabel',
    },
};

const propertyTypeListTranslationKeys: {
    [K in AreaSubscriptionListFilter]: AreaSubscriptionListTranslation;
} = {
    subscribed: {},
    available: {
        listTitle: 'AreaSubscription.Modal.Step3.AvailableListTitle',
        selectItemToggleLabel: 'AreaSubscription.Modal.Step3.SelectItemToggleLabel',
    },
};

const mapToProjectPhaseListData = async (
    projectPhaseIds: string[],
    filter: AreaSubscriptionListFilter,
): Promise<AreaSubscriptionListProjectPhaseData> => {
    const { projectPhases } = await extendedArea.getData(window.__ENVIRONMENT_DETAILS__.areaId);
    const listData: AreaSubscriptionListProjectPhaseData = [];

    let checkboxLabel: string | undefined;
    if (filter === 'available') {
        checkboxLabel = await translateItem(
            projectPhaseListTranslationKeys[filter].selectItemToggleLabel!,
        );
    }

    for (const projectPhaseId of projectPhaseIds) {
        const projectPhase = projectPhases.find(({ id }) => id === projectPhaseId);
        if (!projectPhase) continue;

        const { id, name, mainImage } = projectPhase;
        const status = formatProjectPhaseStatus(projectPhase);

        listData.push({
            id,
            title: name || '',
            status,
            checkboxLabel,
            image: mainImage?.url,
            children: await renderProjectPhaseDetail(projectPhase),
            classes: 'area-subscription-list-card--project-phase',
        });
    }

    return listData;
};

const mapToPropertyTypeListData = async (
    projectPhaseIds: string[],
    filter: AreaSubscriptionListFilter,
): Promise<AreaSubscriptionListPropertyTypeData> => {
    const { projectPhases } = await extendedArea.getData(window.__ENVIRONMENT_DETAILS__.areaId);
    const listData: AreaSubscriptionListPropertyTypeData = [];

    let checkboxLabel: string | undefined;
    if (filter === 'available') {
        checkboxLabel = await translateItem(
            propertyTypeListTranslationKeys[filter].selectItemToggleLabel!,
        );
    }

    const { propertyTypes: subscribedPropertyIds } = getSubscriptionState();

    for (const projectPhaseId of projectPhaseIds) {
        const projectPhase = projectPhases.find(({ id }) => id === projectPhaseId);
        if (!projectPhase) continue;

        const { id, name, propertyTypes } = projectPhase;

        const availableProperties = propertyTypes?.filter(
            ({ id }) => !subscribedPropertyIds.includes(id),
        );

        if (!availableProperties?.length) continue;

        const header: AreaSubscriptionListHeaderInterface = {
            id,
            itemsCount: propertyTypes?.length || 0,
            title: name || '',
            status: formatProjectPhaseStatus(projectPhase),
        };

        const listItems: AreaSubscriptionListCardInterface[] = [];
        for (const propertyType of availableProperties) {
            const { id, name, mainImage } = propertyType;
            const status = formatPropertyTypeStatus(propertyType);

            listItems.push({
                id,
                title: name || '',
                status,
                checkboxLabel,
                image: mainImage?.url,
                children: renderPropertyTypeDetail(propertyType),
                classes: 'area-subscription-list-card--property-type',
            });
        }

        listData.push({ header, listItems });
    }

    return listData;
};

const getListData = async (
    state: ExtendedAreaSubscriptionState,
    itemType: AreaSubscriptionListItemType,
): Promise<AreaSubscriptionListDataFilterInterface> => {
    const { subscribedProjectIds, availableProjectIds } = state;

    const data: AreaSubscriptionListDataFilterInterface = {};
    const translationOptions = getTranslationOptions();

    switch (itemType) {
        case 'projectPhase': {
            const translationKeys = projectPhaseListTranslationKeys;
            if (subscribedProjectIds?.length) {
                data.subscribed = {
                    ...(await translateItems(translationKeys.subscribed, translationOptions)),
                    listData: await mapToProjectPhaseListData(subscribedProjectIds, 'subscribed'),
                };
            }
            if (availableProjectIds?.length) {
                data.available = {
                    ...(await translateItems(translationKeys.available, translationOptions)),
                    listData: await mapToProjectPhaseListData(availableProjectIds, 'available'),
                };
            }
            break;
        }
        case 'propertyType': {
            const translationKeys = propertyTypeListTranslationKeys;
            if (subscribedProjectIds?.length) {
                data.available = {
                    ...(await translateItems(translationKeys.available, translationOptions)),
                    listData: await mapToPropertyTypeListData(subscribedProjectIds, 'available'),
                };
            }
            break;
        }
    }

    return data;
};

export { getListData };
